import { Link } from 'react-router';
import { Notice } from '@p1plug/ui';
import { C, Code, DocPage, P, Snippet } from '../doc';
import {
  createOrder,
  firstRequest,
  latestReading,
  orderResponse,
  readingResponse,
  registerWebhook,
  verifyWebhook,
} from '../samples';

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="dev-step" id={`step-${n}`}>
      <h2 className="p1x-section-title">
        <span className="dev-step-n">{n}</span>
        {title}
      </h2>
      <div className="dev-step-body">{children}</div>
    </section>
  );
}

export function Quickstart() {
  return (
    <DocPage
      title="Quickstart"
      lead="From credentials to a delivered event. Six steps; the first three take five minutes."
    >
      <Step n={1} title="Get access to a test environment">
        <P>
          You need a base URL and a credential for your organisation. For first calls that is an API key (<C>p1k_…</C>):
          one secret, every permission, nothing to configure.
        </P>
        <P>Put both in your environment. Never put a credential in source code or in a browser.</P>
        <Snippet
          title="Shell"
          code={`export P1PLUG_API_URL="https://…"   # the base URL from your onboarding\nexport P1PLUG_API_KEY="p1k_…"`}
        />
        <Notice tone="info" title="Self-service sandbox access is on its way">
          Today your P1Plug contact issues test credentials. The <Link to="/docs/sandbox">sandbox</Link> page describes
          what you will be able to do yourself.
        </Notice>
      </Step>

      <Step n={2} title="Make your first request">
        <P>
          Ask the API who you are. A <C>200</C> with your organisation’s name means the URL, the credential and your
          network path are all correct.
        </P>
        <Code samples={firstRequest} />
        <P>
          A <C>401</C> means the credential is missing, mistyped or revoked. See <Link to="/docs/errors">Errors</Link>{' '}
          for every code.
        </P>
      </Step>

      <Step n={3} title="Create an order">
        <P>
          An order asks P1Plug to send a device to one of your customers. Use your own order number as{' '}
          <C>external_order_id</C>: it makes the call idempotent, so a timeout can always be answered with a retry.
        </P>
        <Code samples={createOrder} />
        <Snippet title="Response" code={orderResponse} />
        <P>
          Store <C>meter_point_id</C> next to your customer. Everything you read later goes through it.
        </P>
      </Step>

      <Step n={4} title="Follow fulfilment and activation">
        <P>
          The order now moves through <C>awaiting_device</C> → <C>device_assigned</C> → <C>packed</C> → <C>shipped</C> →{' '}
          <C>delivered</C> → <C>activated</C>. You do nothing: P1Plug picks and ships, and the device activates itself
          the first time it is plugged into the smart meter.
        </P>
        <P>
          In a test environment P1Plug moves your test orders through these steps with a virtual device, so your
          integration sees exactly the events a real delivery produces. In the sandbox you will trigger each step, and
          each failure, yourself.
        </P>
        <P>
          You can poll <C>GET /api/v1/orders/{'{id}'}</C>, but events are the better way to follow along. That is the
          next step.
        </P>
      </Step>

      <Step n={5} title="Receive events">
        <P>Register one HTTPS endpoint. P1Plug posts batches of events to it, signed with a secret you choose.</P>
        <Code samples={registerWebhook} />
        <P>
          In your handler, verify the signature on the raw body, process each event once, and answer with any <C>2xx</C>
          .
        </P>
        <Code samples={verifyWebhook} />
        <P>
          Not ready to expose an endpoint? Read the same events at your own pace with the cursor API:{' '}
          <Link to="/docs/webhooks#cursor">Webhooks and events</Link>.
        </P>
      </Step>

      <Step n={6} title="Read meter data">
        <P>
          Once <C>installation.activated</C> arrives, readings flow. They arrive as <C>telemetry.electricity</C> events,
          and the latest one is always available on request.
        </P>
        <Code samples={latestReading} />
        <Snippet title="Response" code={readingResponse} />
        <P>
          Units are integers: watts, watt-hours, tenths of a volt (<C>voltage_dv</C>), milliamperes (<C>current_ma</C>).
          A meter that does not report a value leaves the field out; older meters have no <C>phases</C>.
        </P>
      </Step>

      <section className="dev-step">
        <h2 className="p1x-section-title">Before you go to production</h2>
        <ul className="p1x-prose">
          <li>
            Replace the API key with an OAuth machine client that carries only the scopes each service needs:{' '}
            <Link to="/docs/authentication">Authentication</Link>.
          </li>
          <li>
            Make event handling idempotent on <C>event_id</C> and persist your cursor: deliveries are at-least-once.
          </li>
          <li>
            Treat <C>5xx</C>, <C>429</C> and timeouts as retryable, and everything else as a fault to fix:{' '}
            <Link to="/docs/errors">Errors</Link>.
          </li>
          <li>
            Handle <C>installation.replaced</C>: the meter point stays, the device changes.
          </li>
        </ul>
      </section>
    </DocPage>
  );
}
